import { useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";

export enum StepId {
  SUPPORT = "support",
  MESSAGING = "messaging",
  AGENTS = "agents",
  SCORING = "scoring",
  REVIEW = "review",
}

export interface Step {
  id: StepId;
  tab: string;
  title: string;
  description: string;
}

export const STEPS: Step[] = [
  {
    id: StepId.SUPPORT,
    tab: "Support Platform",
    title: "Connect Your Support Platform",
    description:
      "Link your Intercom account to sync customer conversations and agent interactions. We'll be adding other popular support platforms soon.",
  },
  {
    id: StepId.MESSAGING,
    tab: "Messaging Platform",
    title: "Connect your team's communication platform",
    description:
      "Connect Slack to enable team notifications and real-time updates. We'll be adding more team chat platforms soon.",
  },
  {
    id: StepId.AGENTS,
    tab: "Agents",
    title: "Setup your employees to receive daily scores",
    description:
      "Choose which employees to include in your daily scores.",
  },
  {
    id: StepId.SCORING,
    tab: "Scoring",
    title: "Your scoring preferences",
    description: "Configure how agent performance is measured and scored.",
  },
  {
    id: StepId.REVIEW,
    tab: "Review",
    title: "Check your scoring system",
    description:
      "Preview how your employees' scores will look based on your configuration. If anything doesn't look right, you can go back and adjust your scoring preferences.",
  },
];

interface UseOnboardingStepsOptions {
  supportConnected: boolean;
  messagingConnected: boolean;
  saving: boolean;
}

export function useOnboardingSteps({
  supportConnected,
  messagingConnected,
  saving,
}: UseOnboardingStepsOptions) {
  const [searchParams, setSearchParams] = useSearchParams();

  const getInitialState = useCallback((): {
    step: number;
    completed: Set<number>;
  } => {
    const stepParam = searchParams.get("step");
    if (stepParam) {
      const stepIndex = STEPS.findIndex((s) => s.id === stepParam);
      if (stepIndex > 0) {
        const completed = new Set<number>();
        for (let i = 0; i < stepIndex; i++) {
          completed.add(i);
        }
        return { step: stepIndex, completed };
      }
    }
    return { step: 0, completed: new Set<number>() };
  }, [searchParams]);

  const [initialState] = useState(getInitialState);
  const [currentStep, setCurrentStep] = useState(initialState.step);
  const [completedSteps, setCompletedSteps] = useState<Set<number>>(
    initialState.completed
  );

  const currentStepData = STEPS[currentStep];
  const currentStepId = currentStepData.id;
  const isLastStep = currentStep === STEPS.length - 1;

  const navigateToStep = useCallback(
    (stepIndex: number) => {
      setCurrentStep(stepIndex);
      setSearchParams({ step: STEPS[stepIndex].id }, { replace: true });
    },
    [setSearchParams]
  );

  const handleBack = useCallback(() => {
    if (currentStep > 0) {
      navigateToStep(currentStep - 1);
    }
  }, [currentStep, navigateToStep]);

  const canNavigateToStep = useCallback(
    (stepIndex: number): boolean => {
      if (stepIndex === currentStep || completedSteps.has(stepIndex)) {
        return true;
      }
      for (let i = 0; i < stepIndex; i++) {
        if (!completedSteps.has(i)) return false;
      }
      return true;
    },
    [currentStep, completedSteps]
  );

  const isStepDisabled = useCallback(
    (stepIndex: number): boolean => !canNavigateToStep(stepIndex),
    [canNavigateToStep]
  );

  const handleTabChange = useCallback(
    (value: string) => {
      const stepIndex = STEPS.findIndex((step) => step.id === value);
      if (stepIndex !== -1 && canNavigateToStep(stepIndex)) {
        navigateToStep(stepIndex);
      }
    },
    [canNavigateToStep, navigateToStep]
  );

  const markCurrentCompleteAndAdvance = useCallback(
    (onComplete: () => void) => {
      const newCompleted = new Set(completedSteps);
      newCompleted.add(currentStep);
      setCompletedSteps(newCompleted);

      if (currentStep < STEPS.length - 1) {
        navigateToStep(currentStep + 1);
      } else {
        onComplete();
      }
    },
    [completedSteps, currentStep, navigateToStep]
  );

  const isAwaitingSupportConnection =
    currentStepId === StepId.SUPPORT && !supportConnected;
  const isAwaitingMessagingConnection =
    currentStepId === StepId.MESSAGING && !messagingConnected;
  const isConnectionStep =
    currentStepId === StepId.SUPPORT || currentStepId === StepId.MESSAGING;
  const isConnected =
    (currentStepId === StepId.SUPPORT && supportConnected) ||
    (currentStepId === StepId.MESSAGING && messagingConnected);

  const isContinueDisabled =
    saving || isAwaitingSupportConnection || isAwaitingMessagingConnection;

  const buttonText = saving
    ? "Saving..."
    : isLastStep
      ? "Activate Setup"
      : isConnectionStep && isConnected
        ? "Continue"
        : "Save and Continue";

  return {
    currentStep,
    currentStepData,
    currentStepId,
    isLastStep,
    completedSteps,
    navigateToStep,
    handleBack,
    handleTabChange,
    isStepDisabled,
    isContinueDisabled,
    buttonText,
    markCurrentCompleteAndAdvance,
  };
}
